import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { FormRenderer } from "@/components/forms/FormRenderer";
import { Plus, Search, FileText, Copy, Eye, MoreHorizontal, Archive, ListChecks, Layers, PencilLine, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = { published: "bg-emerald-100 text-emerald-700", draft: "bg-amber-100 text-amber-700", superseded: "bg-slate-100 text-slate-500", archived: "bg-slate-100 text-slate-400" };
const SCOPE_LABEL = { system: "System", account: "Account", group: "Group" };

function StatusBadge({ s }) { return <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_COLORS[s] || "bg-slate-100 text-slate-600"}`}>{s}</span>; }

export default function FormsAdmin() {
  const navigate = useNavigate();
  const { isSuper, isAdmin } = useAuth();
  const [forms, setForms] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("all");
  const [status, setStatus] = useState("all");
  const [creating, setCreating] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    try {
      const [f, t, c] = await Promise.all([
        api.get("/forms"), api.get("/forms/templates"), api.get("/forms/field-catalog"),
      ]);
      setForms(f.data.items); setTemplates(t.data.items); setCatalog(c.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = forms.filter((f) =>
    (scope === "all" || f.scope_type === scope) &&
    (status === "all" || f.status === status) &&
    (!q || f.name.toLowerCase().includes(q.toLowerCase())));

  const doPreview = async (id) => {
    try { const { data } = await api.get(`/forms/${id}/preview`); setPreview(data); }
    catch (e) { toast.error(apiError(e)); }
  };
  const [pick, setPick] = useState(null);
  const [targets, setTargets] = useState([]);
  useEffect(() => { api.get("/forms/scope-targets").then((r) => setTargets((r.data.items || []).filter((t) => t.scope_type !== "system"))).catch(() => {}); }, []);
  const withTarget = (fn) => (targets.length > 1 ? setPick({ fn }) : fn(targets[0] || {}));
  const duplicate = (f) => withTarget(async (t) => {
    try {
      const { data } = await api.post(`/forms/${f.id}/duplicate`, { name: `${f.name} (copy)`, scope_type: t.scope_type, organization_id: t.organization_id });
      toast.success("Form duplicated"); navigate(`/admin/forms/${data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  });
  const applyTemplate = (tpl) => withTarget(async (t) => {
    try {
      const { data } = await api.post("/forms", { template_id: tpl.id, scope_type: t.scope_type, organization_id: t.organization_id });
      toast.success("Form created from template"); navigate(`/admin/forms/${data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  });
  const archive = async (f) => {
    try { await api.post(`/forms/${f.id}/archive`); toast.success("Archived"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="forms-admin">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Forms & Questionnaires</h1>
          <p className="text-sm text-slate-500 mt-1">Create, manage and publish reusable forms — no code required.</p>
        </div>
        <Button className="bg-[#006090] hover:bg-[#004d73] text-white" onClick={() => setCreating(true)} data-testid="forms-new-btn"><Plus className="w-4 h-4 mr-1.5" />New Form</Button>
      </div>

      <Tabs defaultValue="forms">
        <TabsList data-testid="forms-tabs">
          <TabsTrigger value="forms" data-testid="tab-forms">Forms</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="forms" className="mt-4 space-y-4">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search forms…" className="pl-8" data-testid="forms-search" />
            </div>
            <Select value={scope} onValueChange={setScope}><SelectTrigger className="w-[140px]" data-testid="forms-scope-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="all">All Scopes</SelectItem><SelectItem value="system">System</SelectItem><SelectItem value="account">Account</SelectItem><SelectItem value="group">Group</SelectItem></SelectContent></Select>
            <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-[150px]" data-testid="forms-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white"><SelectItem value="all">All Statuses</SelectItem><SelectItem value="draft">Draft</SelectItem><SelectItem value="published">Published</SelectItem><SelectItem value="archived">Archived</SelectItem></SelectContent></Select>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-slate-200 rounded-xl" data-testid="forms-empty">
              <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="font-medium text-slate-600">No forms found</p>
              <p className="text-sm text-slate-400 mt-1">Create a form or start from a template.</p>
            </div>
          ) : (
            <div className="rounded-xl border border-slate-200 overflow-hidden bg-white">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide">
                  <tr><th className="text-left px-4 py-2.5">Name</th><th className="text-left px-4 py-2.5">Scope</th><th className="text-left px-4 py-2.5">Status</th><th className="text-left px-4 py-2.5">Fields</th><th className="text-left px-4 py-2.5">Responses</th><th className="text-left px-4 py-2.5">Owner</th><th className="px-4 py-2.5"></th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((f) => (
                    <tr key={f.id} className="hover:bg-slate-50" data-testid={`form-row-${f.id}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-800">{f.name}</span>
                          {f.critical && <Badge className="bg-[#006090]/10 text-[#006090] gap-1"><ShieldCheck className="w-3 h-3" />Critical</Badge>}
                        </div>
                        <p className="text-xs text-slate-400 mt-0.5 line-clamp-1">{f.description}</p>
                      </td>
                      <td className="px-4 py-3"><Badge variant="secondary">{SCOPE_LABEL[f.scope_type]}</Badge></td>
                      <td className="px-4 py-3"><StatusBadge s={f.status} />{f.has_draft && f.status === "published" && <span className="ml-1 text-[10px] text-amber-600">+draft</span>}</td>
                      <td className="px-4 py-3 text-slate-600">{f.field_count}</td>
                      <td className="px-4 py-3 text-slate-600">{f.response_count}</td>
                      <td className="px-4 py-3 text-slate-500">{f.owner}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {f.can_manage && <Button size="sm" variant="ghost" onClick={() => navigate(`/admin/forms/${f.id}`)} data-testid={`form-edit-${f.id}`}><PencilLine className="w-4 h-4 mr-1" />Edit</Button>}
                          <Button size="sm" variant="ghost" onClick={() => doPreview(f.id)} data-testid={`form-preview-${f.id}`}><Eye className="w-4 h-4" /></Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild><Button size="sm" variant="ghost" data-testid={`form-more-${f.id}`}><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-white">
                              <DropdownMenuItem onClick={() => navigate(`/admin/forms/${f.id}/responses`)} data-testid={`form-responses-${f.id}`}><ListChecks className="w-4 h-4 mr-2" />Responses</DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigate(`/admin/forms/${f.id}/assignments`)} data-testid={`form-assignments-${f.id}`}><ListChecks className="w-4 h-4 mr-2" />Assignments</DropdownMenuItem>
                              {f.can_manage && <DropdownMenuItem onClick={() => duplicate(f)}><Copy className="w-4 h-4 mr-2" />Duplicate</DropdownMenuItem>}
                              {f.can_manage && f.status !== "archived" && <DropdownMenuItem onClick={() => archive(f)} className="text-rose-600"><Archive className="w-4 h-4 mr-2" />Archive</DropdownMenuItem>}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>

        <TabsContent value="templates" className="mt-4">
          <p className="text-sm text-slate-500 mb-4">Start from a Top-Recruit starter template. Duplicating creates an editable copy in your scope.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="templates-grid">
            {templates.map((t) => (
              <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-4 flex flex-col" data-testid={`template-card-${t.id}`}>
                <div className="flex items-start gap-2">
                  <span className="rounded-lg bg-[#006090]/10 text-[#006090] p-2"><Layers className="w-4 h-4" /></span>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-slate-800 text-sm">{t.name}</p>
                    <p className="text-xs text-slate-400">{t.category} · {t.field_count} fields</p>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-2 flex-1 line-clamp-3">{t.template_note || t.description}</p>
                <div className="flex gap-1.5 mt-3">
                  <Button size="sm" className="bg-[#006090] hover:bg-[#004d73] text-white flex-1" onClick={() => applyTemplate(t)} data-testid={`template-use-${t.id}`}>Use Template</Button>
                  <Button size="sm" variant="outline" onClick={() => doPreview(t.id)} data-testid={`template-preview-${t.id}`}><Eye className="w-4 h-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {creating && <NewFormDialog catalog={catalog} onClose={() => setCreating(false)} onCreated={(id) => navigate(`/admin/forms/${id}`)} isSuper={isSuper} />}
      <Dialog open={!!pick} onOpenChange={(v) => !v && setPick(null)}>
        <DialogContent className="bg-white max-w-sm" data-testid="form-target-dialog">
          <DialogHeader><DialogTitle>Copy into which group?</DialogTitle><DialogDescription>The copy is an independent draft owned by you. The original is not changed.</DialogDescription></DialogHeader>
          <div className="space-y-1.5">{targets.map((t, i) => (
            <Button key={i} variant="outline" className="w-full justify-start" onClick={() => { const fn = pick.fn; setPick(null); fn(t); }} data-testid={`form-target-${i}`}>{t.label}</Button>
          ))}</div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="forms-preview-dialog">
          <DialogHeader><DialogTitle>{preview?.name}</DialogTitle>{preview?.description && <DialogDescription>{preview.description}</DialogDescription>}</DialogHeader>
          {preview && <FormRenderer fields={preview.fields} values={{}} disabled />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NewFormDialog({ catalog, onClose, onCreated, isSuper }) {
  const [form, setForm] = useState({ name: "", description: "", form_type: "general" });
  const [targets, setTargets] = useState([]);
  const [ti, setTi] = useState(0);
  useEffect(() => { api.get("/forms/scope-targets").then((r) => setTargets(r.data.items || [])).catch(() => {}); }, []);
  const [busy, setBusy] = useState(false);
  const create = async () => {
    if (!form.name.trim()) { toast.error("Form name is required"); return; }
    const t = targets[ti] || {};
    setBusy(true);
    try { const { data } = await api.post("/forms", { ...form, scope_type: t.scope_type, organization_id: t.organization_id }); toast.success("Form created"); onCreated(data.id); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white" data-testid="new-form-dialog">
        <DialogHeader><DialogTitle>New Form</DialogTitle><DialogDescription>Give it a name and scope — you'll design the fields next.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Form Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-form-name" /></div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="new-form-desc" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Belongs to</Label>
              <Select value={String(ti)} onValueChange={(v) => setTi(Number(v))}><SelectTrigger data-testid="new-form-scope"><SelectValue placeholder="Loading…" /></SelectTrigger>
                <SelectContent className="bg-white">{targets.map((t, i) => <SelectItem key={i} value={String(i)}>{t.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Form Type</Label>
              <Select value={form.form_type} onValueChange={(v) => setForm({ ...form, form_type: v })}><SelectTrigger data-testid="new-form-type"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white">{(catalog?.form_types || []).map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent></Select></div>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#006090] hover:bg-[#004d73] text-white" onClick={create} disabled={busy} data-testid="new-form-create">{busy ? "Creating…" : "Create & Design"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
